import { Client, Osdk } from "@osdk/client";
import {
  QueryClient,
  useQuery,
  useInfiniteQuery,
  UseQueryResult,
} from "@tanstack/react-query";
import { Analysis } from "@unruly-osdk/sdk";

const keys = {
  all: ["analyses"] as const,
  byRequestId: (requestId: string) =>
    [...keys.all, "requestId", requestId] as const,
  byId: (analysisId: string) => [...keys.all, "id", analysisId] as const,
  eddInfinite: (userId: string) =>
    [...keys.all, "eddInfinite", userId] as const,
  tddInfinite: (userId: string) =>
    [...keys.all, "tddInfinite", userId] as const,
};

function AnalysisService(queryClient: QueryClient, apiClient: Client) {
  const service = {
    useGetAnalysisByRequestId: (
      requestId: string,
    ): UseQueryResult<Osdk.Instance<Analysis> | null> => {
      return useQuery({
        queryKey: keys.byRequestId(requestId),
        queryFn: async () => service.getAnalysisByRequestId(requestId),
        enabled: !!requestId,
      });
    },

    getAnalysisByRequestId: async (
      requestId: string,
    ): Promise<Osdk.Instance<Analysis> | null> => {
      const response = await apiClient(Analysis)
        .where({ requestId: requestId })
        .fetchPage({
          $orderBy: { createdAt: "desc" },
        });
      return response.data?.[0] || null;
    },

    useGetAnalysisById: (
      analysisId: string,
    ): UseQueryResult<Osdk.Instance<Analysis> | null> => {
      return useQuery({
        queryKey: keys.byId(analysisId),
        queryFn: async () => service.getAnalysisById(analysisId),
        enabled: !!analysisId,
      });
    },

    getAnalysisById: async (
      analysisId: string,
    ): Promise<Osdk.Instance<Analysis> | null> => {
      const response = await apiClient(Analysis)
        .where({ id: analysisId })
        .fetchPage();
      return response.data?.[0] || null;
    },

    getEventDeepDivesForUser: async (
      userId: string,
      pageSize: number = 20,
      nextPageToken?: string,
    ): Promise<{
      data: Osdk.Instance<Analysis, never, "id" | "title" | "createdAt">[];
      nextPageToken?: string;
    }> => {
      const whereClause: Record<string, unknown> = {
        type: "event deep dive",
        userId: userId,
      };

      const response = await apiClient(Analysis)
        .where(whereClause)
        .fetchPage({
          $select: ["id", "title", "createdAt"],
          $orderBy: { createdAt: "desc" },
          $pageSize: pageSize,
          ...(nextPageToken && { $nextPageToken: nextPageToken }),
        });

      return {
        data: response.data || [],
        nextPageToken: response.nextPageToken,
      };
    },

    getTopicDeepDivesForUser: async (
      userId: string,
      pageSize: number = 20,
      nextPageToken?: string,
    ): Promise<{
      data: Osdk.Instance<Analysis, never, "id" | "title" | "createdAt">[];
      nextPageToken?: string;
    }> => {
      const whereClause: Record<string, unknown> = {
        type: "topic deep dive",
        userId: userId,
      };

      const response = await apiClient(Analysis)
        .where(whereClause)
        .fetchPage({
          $select: ["id", "title", "createdAt"],
          $orderBy: { createdAt: "desc" },
          $pageSize: pageSize,
          ...(nextPageToken && { $nextPageToken: nextPageToken }),
        });

      return {
        data: response.data || [],
        nextPageToken: response.nextPageToken,
      };
    },

    useGetEventDeepDivesInfinite: (userId: string, pageSize: number) => {
      return useInfiniteQuery({
        queryKey: keys.eddInfinite(userId),
        queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
          return service.getEventDeepDivesForUser(userId, pageSize, pageParam);
        },
        getNextPageParam: (lastPage) => lastPage.nextPageToken,
        enabled: !!userId,
        initialPageParam: undefined as string | undefined,
      });
    },

    useGetTopicDeepDivesInfinite: (userId: string, pageSize: number) => {
      return useInfiniteQuery({
        queryKey: keys.tddInfinite(userId),
        queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
          return service.getTopicDeepDivesForUser(userId, pageSize, pageParam);
        },
        getNextPageParam: (lastPage) => lastPage.nextPageToken,
        enabled: !!userId,
        initialPageParam: undefined as string | undefined,
      });
    },

    invalidateEventDeepDives: (userId: string) => {
      queryClient.invalidateQueries({
        queryKey: keys.eddInfinite(userId),
      });
    },

    invalidateTopicDeepDives: (userId: string) => {
      queryClient.invalidateQueries({
        queryKey: keys.tddInfinite(userId),
      });
    },
  };

  return service;
}

export default AnalysisService;
